/*
 * Copyright (C) 2025 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import React from 'react'
import {Flex} from '@instructure/ui-flex'
import {Link} from '@instructure/ui-link'
import {View} from '@instructure/ui-view'
import {useScope as createI18nScope} from '@canvas/i18n'
import {SpeedGraderButton} from '../../buttons/SpeedGraderButton'
import {Navigator} from './Navigator'
import TruncateWithTooltip from '@canvas/instui-bindings/react/TruncateWithTooltip'

const I18n = createI18nScope('LearningMasteryGradebook')

export interface Assignment {
  id: string
  name: string
  htmlUrl: string
}

export interface AssignmentSectionProps {
  courseId: string
  studentId: string
  currentAssignment: Assignment
  hasPrevious: boolean
  hasNext: boolean
  onPrevious: () => void
  onNext: () => void
  nextLabel?: string
  previousLabel?: string
}

export const AssignmentSection: React.FC<AssignmentSectionProps> = ({
  courseId,
  studentId,
  currentAssignment,
  hasPrevious,
  hasNext,
  onPrevious,
  onNext,
  nextLabel = I18n.t('Next assignment'),
  previousLabel = I18n.t('Previous assignment'),
}) => {
  return (
    <Flex direction="column" gap="small">
      <Navigator
        hasPrevious={hasPrevious}
        hasNext={hasNext}
        previousLabel={previousLabel}
        nextLabel={nextLabel}
        onPrevious={onPrevious}
        onNext={onNext}
        data-testid="assignment-navigator"
      >
        <View as="div" display="inline-block" maxWidth="100%">
          <Link
            href={currentAssignment.htmlUrl}
            isWithinText={false}
            target="_blank"
            display="block"
          >
            <TruncateWithTooltip>{currentAssignment.name}</TruncateWithTooltip>
          </Link>
        </View>
      </Navigator>

      <View as="div" textAlign="center">
        <SpeedGraderButton
          courseId={courseId}
          assignmentId={currentAssignment.id}
          studentId={studentId}
        />
      </View>
    </Flex>
  )
}
